import { createFileRoute, Link } from "@tanstack/react-router";
import { breadcrumbLd, germanDateToIso, jsonLdScript, SITE_URL } from "@/lib/json-ld";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Clock,
  ExternalLink,
  Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import mag1 from "@/assets/magazin-1-1024.webp";
import mag2 from "@/assets/magazin-2-1024.webp";
import mag3 from "@/assets/magazin-3-1024.webp";
import mag4 from "@/assets/magazin-4-1024.webp";
import { articleSummaries } from "@/data/article-summaries";
import { getArticle } from "@/lib/articles.functions";
import { useLanguage } from "@/lib/i18n";
import { EigenmietwertCalculator } from "@/components/eigenmietwert-calculator";

const articleImages = [mag1, mag2, mag3, mag4, mag1, mag2, mag3, mag4, mag1] as const;

const detailCopy = {
  de: {
    back: "Zurück zum Magazin",
    overview: "Überblick",
    checklist: "Checkliste",
    sources: "Quellen",
    sourceNote:
      "Die Angaben sind als Orientierung für Schweizer Bau- und Wohnprojekte gedacht. Verbindliche Preise und Förderbeiträge müssen objekt- und kantonsspezifisch geprüft werden.",
    notFoundTitle: "Artikel nicht gefunden",
    notFoundText: "Der gewünschte Artikel existiert nicht oder wurde verschoben.",
    notFoundButton: "Magazin öffnen",
    ctaTitle: "Offerten für Ihr Projekt einholen?",
    ctaText:
      "Beschreiben Sie Ihr Vorhaben kostenlos und erhalten Sie passende Rückmeldungen von geprüften Auftragnehmern.",
    ctaButton: "Anfrage eingeben",
  },
  fr: {
    back: "Retour au magazine",
    overview: "Apercu",
    checklist: "Check-list",
    sources: "Sources",
    sourceNote:
      "Les indications servent d'orientation pour des projets de construction et d'habitat en Suisse. Les prix et subventions doivent etre verifies selon l'objet et le canton.",
    notFoundTitle: "Article introuvable",
    notFoundText: "L'article demande n'existe pas ou a ete deplace.",
    notFoundButton: "Ouvrir le magazine",
    ctaTitle: "Obtenir des offres pour votre projet?",
    ctaText: "Decrivez gratuitement votre projet et recevez des reponses de prestataires verifies.",
    ctaButton: "Publier un mandat",
  },
  it: {
    back: "Torna alla rivista",
    overview: "Panoramica",
    checklist: "Checklist",
    sources: "Fonti",
    sourceNote:
      "Le indicazioni sono orientative per progetti edilizi e abitativi in Svizzera. Prezzi e incentivi vanno verificati in base all'oggetto e al cantone.",
    notFoundTitle: "Articolo non trovato",
    notFoundText: "L'articolo richiesto non esiste o e stato spostato.",
    notFoundButton: "Apri rivista",
    ctaTitle: "Richiedere offerte per il suo progetto?",
    ctaText: "Descriva gratuitamente il suo progetto e riceva risposte da imprese verificate.",
    ctaButton: "Pubblica un incarico",
  },
  en: {
    back: "Back to the magazine",
    overview: "Overview",
    checklist: "Checklist",
    sources: "Sources",
    sourceNote:
      "The information is intended as guidance for Swiss construction and housing projects. Binding prices and subsidies must be checked for the specific property and canton.",
    notFoundTitle: "Article not found",
    notFoundText: "The requested article does not exist or has been moved.",
    notFoundButton: "Open magazine",
    ctaTitle: "Get quotes for your project?",
    ctaText: "Describe your project free of charge and hear from verified contractors.",
    ctaButton: "Post a job",
  },
} as const;

export const Route = createFileRoute("/artikel/$slug")({
  staticData: { sitemap: true },
  loader: ({ params }) => getArticle({ data: { slug: params.slug } }),
  head: ({ loaderData: article }) => {
    return {
      meta: [
        { title: article ? `${article.title}. Begner Magazin` : "Artikel. Begner Magazin" },
        {
          name: "description",
          content:
            article?.excerpt ?? "Ratgeber rund um Bauen, Renovieren und Wohnen in der Schweiz.",
        },
        { property: "og:title", content: article?.title ?? "Begner Magazin" },
        {
          property: "og:description",
          content: article?.excerpt ?? "Ratgeber rund um Bauen und Renovieren.",
        },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      scripts: article
        ? [
            jsonLdScript({
              "@context": "https://schema.org",
              "@type": "Article",
              headline: article.title,
              description: article.excerpt,
              inLanguage: "de-CH",
              mainEntityOfPage: `${SITE_URL}/artikel/${article.slug}`,
              datePublished: germanDateToIso(article.date),
              dateModified: germanDateToIso(article.date),
              image: `${SITE_URL}${articleImages[article.imageIndex]}`,
              author: { "@type": "Organization", name: "Begner Redaktion", url: SITE_URL },
              reviewedBy: { "@type": "Organization", name: "Begner Redaktion" },
              citation: article.sources.map((source) => source.url),
              publisher: { "@type": "Organization", name: "Begner", url: SITE_URL },
            }),
            jsonLdScript(
              breadcrumbLd([
                { name: "Startseite", path: "/" },
                { name: "Magazin", path: "/artikel" },
                { name: article.title, path: `/artikel/${article.slug}` },
              ]),
            ),
          ]
        : [],
    };
  },
  component: ArticleDetailPage,
});

function ArticleDetailPage() {
  const { language } = useLanguage();
  const copy = detailCopy[language];
  const article = Route.useLoaderData();

  if (!article) {
    return (
      <section className="bg-surface py-20">
        <div className="container-page max-w-2xl text-center">
          <h1 className="font-display text-4xl font-bold">{copy.notFoundTitle}</h1>
          <p className="mt-4 text-muted-foreground">{copy.notFoundText}</p>
          <Button asChild className="mt-8 font-semibold">
            <Link to="/artikel">
              {copy.notFoundButton}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </section>
    );
  }

  const image = articleImages[article.imageIndex];
  const relatedArticles = articleSummaries
    .filter((item) => item.slug !== article.slug && item.categoryId === article.categoryId)
    .slice(0, 2);

  return (
    <>
      <section className="border-b border-border bg-surface">
        <div className="container-page py-8 md:py-10">
          <Link
            to="/artikel"
            search={{ category: article.categoryId }}
            className="inline-flex items-center gap-2 text-sm font-semibold text-brand-deep transition hover:text-brand"
          >
            <ArrowLeft className="h-4 w-4" />
            {copy.back}
          </Link>

          <div className="mt-8 grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
            <div>
              <Badge className="bg-brand-soft text-brand-deep hover:bg-brand-soft">
                {article.category}
              </Badge>
              <h1 className="mt-5 max-w-4xl font-display text-4xl font-bold tracking-tight md:text-5xl">
                {article.title}
              </h1>
              <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{article.excerpt}</p>
              <div className="mt-7 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="h-4 w-4" />
                  {article.date}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  {article.readingTime}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Tag className="h-4 w-4" />
                  {article.category}
                </span>
              </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
              <img
                src={image}
                alt={`${article.title}. Ratgeber für Bauen und Renovieren in der Schweiz`}
                width={1024}
                height={704}
                className="aspect-[16/11] h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background py-12 md:py-16">
        <div className="container-page grid gap-10 lg:grid-cols-[minmax(0,1fr)_320px]">
          <article className="min-w-0">
            {article.takeaways.length > 0 && (
              <div className="mb-8 rounded-2xl border border-brand/30 bg-brand-soft p-6">
                <h2 className="font-display text-lg font-bold text-brand-deep">
                  Das Wichtigste in Kürze
                </h2>
                <ul className="mt-3 list-disc space-y-2 pl-5 leading-7">
                  {article.takeaways.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-muted-foreground">
                  Von der Begner Redaktion geprüft, Stand {article.date}.
                </p>
              </div>
            )}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
              <h2 className="font-display text-2xl font-bold">{copy.overview}</h2>
              <div className="mt-5 space-y-4 text-base leading-8 text-muted-foreground">
                {article.intro.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>

            {article.calculator === "eigenmietwert" && <EigenmietwertCalculator />}

            <div className="mt-10 space-y-10">
              {article.sections.map((section) => (
                <section key={section.heading}>
                  <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">
                    {section.heading}
                  </h2>
                  <div className="mt-4 space-y-4 text-base leading-8 text-muted-foreground">
                    {section.paragraphs.map((paragraph) => (
                      <p key={paragraph}>{paragraph}</p>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            <section className="mt-12 border-t border-border pt-8">
              <h2 className="font-display text-2xl font-bold">{copy.sources}</h2>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                {article.sourceNote ?? copy.sourceNote}
              </p>
              <ul className="mt-5 space-y-3">
                {article.sources.map((source) => (
                  <li key={source.url}>
                    <a
                      href={source.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 break-words text-sm font-semibold text-brand-deep underline-offset-4 hover:underline"
                    >
                      {source.label}
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          </article>

          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">{copy.overview}</h2>
              <ul className="mt-4 space-y-3">
                {article.takeaways.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-6 text-muted-foreground">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-deep" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-border bg-surface p-6">
              <h2 className="font-display text-xl font-bold">{article.checklistTitle}</h2>
              <ul className="mt-4 space-y-3">
                {article.checklist.map((item) => (
                  <li key={item} className="flex gap-3 text-sm leading-6 text-muted-foreground">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {relatedArticles.length > 0 && (
              <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
                <h2 className="font-display text-xl font-bold">Mehr zum Thema</h2>
                <div className="mt-4 space-y-4">
                  {relatedArticles.map((item) => (
                    <Link
                      key={item.slug}
                      to="/artikel/$slug"
                      params={{ slug: item.slug }}
                      className="block text-sm font-semibold leading-6 text-foreground transition hover:text-brand-deep"
                    >
                      {item.title}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-hero py-16">
        <div className="container-page relative grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-center">
          <div>
            <h2 className="font-display text-3xl font-bold text-white md:text-4xl">
              {copy.ctaTitle}
            </h2>
            <p className="mt-3 max-w-xl text-white/85">{copy.ctaText}</p>
          </div>
          <div className="md:text-right">
            <Button asChild size="lg" variant="secondary" className="font-semibold">
              <Link to="/auftrag-einstellen">
                {copy.ctaButton}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
